import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { publicApi } from '../api';

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem('aegis_lang');
    if (saved) return saved;
    const browserLang = (typeof navigator !== 'undefined' ? (navigator.language || navigator.userLanguage || '') : '').toLowerCase();
    return browserLang.startsWith('tr') ? 'tr' : 'en';
  });
  const [t, setT] = useState({});
  const [meta, setMeta] = useState(null);
  const [ready, setReady] = useState(false);

  const setLang = useCallback((code) => {
    const validCode = typeof code === 'string' && code ? code : (code?.code || 'tr');
    localStorage.setItem('aegis_lang', validCode);
    setLangState(validCode);
    // update document
    document.documentElement.lang = validCode;
    document.documentElement.dir = validCode === 'ar' ? 'rtl' : 'ltr';
  }, []);

  useEffect(() => {
    const safeLang = lang || 'tr';
    setReady(false);
    Promise.all([
      publicApi.translations(safeLang).catch(() => ({})),
      publicApi.meta(safeLang).catch(() => null),
    ]).then(([trData, m]) => {
      setT(trData || {});
      if (m) setMeta(m);
      setReady(true);
      document.documentElement.lang = safeLang;
      document.documentElement.dir = safeLang === 'ar' ? 'rtl' : 'ltr';
    });
  }, [lang]);

  const tr = useCallback((key, fallback) => t[key] || fallback || key, [t]);

  const value = useMemo(() => ({ lang, setLang, t, tr, meta, ready }), [lang, setLang, t, tr, meta, ready]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error('useI18n must be inside I18nProvider');
  return ctx;
};
